import { Goal, goalInboxID, Task } from '@griply/types';

export type GoalGroupedTasks = {
  goalId: string;
  goal: Goal | null;
  tasks: Task[];
};

export type GroupTasksByGoalOptions = {
  tasks: Task[];
  goals: Goal[];
  goalSorting?: string[];
};

export const groupTasksByGoal = (
  options: GroupTasksByGoalOptions,
): GoalGroupedTasks[] => {
  const { tasks, goals, goalSorting } = options;

  // 1. Create Map of goalId -> Task[]
  const groupMap = new Map<string, Task[]>();

  // Initialize with all goals
  for (const goal of goals) {
    groupMap.set(goal.id, []);
  }

  // Add inbox section
  groupMap.set(goalInboxID, []);

  // 2. Assign tasks to groups
  for (const task of tasks) {
    const targetId =
      task.goalId && groupMap.has(task.goalId) ? task.goalId : goalInboxID;
    const group = groupMap.get(targetId);
    if (group) {
      group.push(task);
    }
  }

  // 3. Filter out empty groups
  const nonEmptyGroups: GoalGroupedTasks[] = [];
  for (const [goalId, taskList] of groupMap.entries()) {
    if (!taskList.length) {
      continue;
    }

    const goal = goals.find((g) => g.id === goalId) ?? null;
    nonEmptyGroups.push({
      goalId,
      goal,
      tasks: taskList,
    });
  }

  // 4. Sort groups by goalSorting order
  if (goalSorting?.length) {
    nonEmptyGroups.sort((a, b) => {
      // Inbox always last
      if (a.goalId === goalInboxID) {
        return 1;
      }
      if (b.goalId === goalInboxID) {
        return -1;
      }

      const indexA = goalSorting.indexOf(a.goalId);
      const indexB = goalSorting.indexOf(b.goalId);

      // Items not in sorting array go to end (before inbox)
      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    });
  }

  return nonEmptyGroups;
};
